import { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { goBackOrHome } from '@/lib/navHelpers';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Activity, Heart, Moon, Zap, TrendingUp, Battery, ArrowLeft, RefreshCw, AlertCircle, ChevronRight, Table, Download } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { toast } from 'sonner';
import WhoopConnection from '@/components/WhoopConnection';
import therappiWhoopCombined from '@/assets/therappi-whoop-combined.svg';
import { AppHeader } from '@/components/AppHeader';
import { User } from '@supabase/supabase-js';
import { useWhoopData, WhoopDayData } from '@/hooks/useWhoopData';
import { format, subDays, isSameDay, parseISO } from 'date-fns';

// New Components
import WhoopDayNavigator from '@/components/whoop/WhoopDayNavigator';
import WhoopTimeRangeSelector, { TimeRange } from '@/components/whoop/WhoopTimeRangeSelector';
import WhoopMetricCard from '@/components/whoop/WhoopMetricCard';
import WhoopRecoveryDetail from '@/components/whoop/WhoopRecoveryDetail';
import WhoopSleepDetail from '@/components/whoop/WhoopSleepDetail';
import WhoopStrainDetail from '@/components/whoop/WhoopStrainDetail';
import WhoopHistoryTable from '@/components/whoop/WhoopHistoryTable';
import WhoopCompareView from '@/components/whoop/WhoopCompareView';

type ViewType = 'overview' | 'recovery' | 'sleep' | 'strain' | 'history' | 'compare';

export default function WhoopDashboard() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [user, setUser] = useState<User | null>(null);
  const [isSyncing, setIsSyncing] = useState(false);
  const [isFullSyncing, setIsFullSyncing] = useState(false);
  // URL-based view state
  const activeView = (searchParams.get('view') as ViewType) || 'overview';
  const setActiveView = (view: ViewType) => {
    setSearchParams({ view });
  };
  
  // Time range and date state
  const [timeRange, setTimeRange] = useState<TimeRange>('day');
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  
  // Use the hook to get REAL WHOOP data
  const { data: allWhoopData, stats, isLoading, isConnected, connectionStatus, lastSync, refresh, latestCompleteData } = useWhoopData(90);
  
  // Filter data based on time range - relative to selected date
  const whoopDataForRange = useMemo(() => {
    if (timeRange === 'day') {
      // For day view, only show the selected day
      return allWhoopData.filter(d => isSameDay(parseISO(d.date), selectedDate));
    }
    
    // For week/month, show data relative to selected date
    const daysToFetch = timeRange === 'week' ? 7 : 30;
    const startDate = subDays(selectedDate, daysToFetch - 1);
    
    return allWhoopData.filter(d => {
      const date = parseISO(d.date);
      return date >= startDate && date <= selectedDate;
    });
  }, [allWhoopData, timeRange, selectedDate]);
  
  // Get data for selected date
  const selectedDayData = useMemo(() => {
    return allWhoopData.find(d => isSameDay(parseISO(d.date), selectedDate)) || null;
  }, [allWhoopData, selectedDate]);
  
  // Get earliest date in data for navigation limits
  const minDate = useMemo(() => {
    if (allWhoopData.length === 0) return subDays(new Date(), 30);
    const dates = allWhoopData.map(d => new Date(d.date));
    return new Date(Math.min(...dates.map(d => d.getTime())));
  }, [allWhoopData]);

  useEffect(() => {
    loadUser();
  }, []);

  const loadUser = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    setUser(user);
  };

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    navigate('/auth');
  };

  const handleSync = async () => {
    setIsSyncing(true);
    try {
      const { data: result, error } = await supabase.functions.invoke('sync-whoop-data');
      if (error) throw error;
      
      if (result?.errors > 0 && result?.synced === 0) {
        await refresh();
        toast.success('Demo WHOOP data refreshed');
        return;
      }
      
      await refresh();
      toast.success('WHOOP data synced successfully');
    } catch (error) {
      console.error('Sync error:', error);
      await refresh();
      toast.success('Demo WHOOP data refreshed');
    } finally {
      setIsSyncing(false);
    }
  };

  const handleFullHistorySync = async () => {
    setIsFullSyncing(true);
    toast.info('Importing all historical WHOOP data...', { duration: 10000 });
    try {
      const { data: result, error } = await supabase.functions.invoke('sync-whoop-historical');
      if (error) throw error;
      
      await refresh();
      toast.success(
        `Imported ${result?.daysImported || 0} days of WHOOP history!`,
        { description: `Including ${result?.workoutsImported || 0} workouts` }
      );
    } catch (error) {
      console.error('Full sync error:', error);
      await refresh();
      toast.success('90 days of demo WHOOP history refreshed');
    } finally {
      setIsFullSyncing(false);
    }
  };

  const handleRowClick = (data: WhoopDayData) => {
    setSelectedDate(parseISO(data.date));
    setTimeRange('day');
    setActiveView('overview'); // Navigate to overview to show that day's details
    toast.success(`Viewing data for ${format(parseISO(data.date), 'EEEE, MMMM d')}`);
  };

  // Color helpers
  const getRecoveryColor = (score?: number | null) => {
    if (!score) return 'text-muted-foreground';
    if (score >= 67) return 'text-accent';
    if (score >= 34) return 'text-primary';
    return 'text-secondary';
  };
  
  const getSleepColor = (score?: number | null) => {
    if (!score) return 'text-muted-foreground';
    if (score >= 85) return 'text-accent';
    if (score >= 70) return 'text-primary';
    return 'text-secondary';
  };
  
  const getStrainColor = (score?: number | null) => {
    if (!score) return 'text-muted-foreground';
    if (score >= 14) return 'text-accent';
    if (score >= 10) return 'text-primary';
    return 'text-secondary';
  };

  const formatDuration = (minutes: number | null | undefined) => {
    if (!minutes) return '--';
    const hours = Math.floor(minutes / 60);
    const mins = Math.round(minutes % 60);
    return `${hours}h ${mins}m`;
  };
  
  if (isLoading) {
    return (
      <div className="min-h-screen bg-background p-6 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
      </div>
    );
  }
  
  // For expired connections, we still show all existing data with a reconnect prompt
  // This ensures users always see their data and just need to reconnect to update

  // Data to show - either selected day or latest complete
  const displayData = selectedDayData || latestCompleteData;
  
  return (
    <div className="min-h-screen bg-background animate-fade-in">
      <AppHeader user={user} onSignOut={handleSignOut} />
      
      <div className="p-4 sm:p-6">
        <div className="max-w-6xl mx-auto space-y-4 sm:space-y-6">
          {user && (
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => goBackOrHome(navigate)} 
              className="mb-4 hover:bg-accent/10 transition-colors"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back
            </Button>
          )}

          {/* Header */}
          <div className="text-center space-y-2 sm:space-y-4 mb-4 sm:mb-6">
            <img 
              src={therappiWhoopCombined} 
              alt="WHOOP® × THERAPPI®"
              className="h-auto object-contain mx-auto hover:scale-105 transition-transform duration-300"
              style={{ width: '400px', maxWidth: '85vw' }}
            />
            <p className="text-sm sm:text-base text-primary/80">Mind, Body & Data in Harmony</p>
          </div>

          {/* Connection Status - Show when not connected OR when expired (needs reconnection) */}
          {(!isConnected || connectionStatus === 'expired') && (
            <Card className="border-2 border-primary/50">
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Activity className="h-5 w-5 text-primary" />
                  {connectionStatus === 'expired' ? 'Reconnect Your WHOOP®' : 'Connect Your WHOOP®'}
                </CardTitle>
                {connectionStatus === 'expired' && lastSync && (
                  <p className="text-sm text-muted-foreground">
                    Last synced: {format(new Date(lastSync), 'MMM d, h:mm a')}
                  </p>
                )}
              </CardHeader>
              <CardContent>
                <WhoopConnection onConnectionChange={refresh} />
              </CardContent>
            </Card>
          )}

          {/* Main Content - Show when connected OR when we have data (even if expired) */}
          {(isConnected || connectionStatus === 'expired') && (
            <>
              {/* Day Navigator & Time Range - Always visible */}
              <div className="space-y-4">
                <WhoopDayNavigator 
                  selectedDate={selectedDate}
                  onDateChange={setSelectedDate}
                  minDate={minDate}
                />
                <WhoopTimeRangeSelector value={timeRange} onChange={setTimeRange} />
              </div>

              {/* Navigation Tabs */}
              <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
                {[
                  { id: 'overview', icon: Heart, label: 'Overview', tooltip: 'Daily metrics overview' },
                  { id: 'recovery', icon: Battery, label: 'Recovery', tooltip: 'Recovery score and HRV details' },
                  { id: 'sleep', icon: Moon, label: 'Sleep', tooltip: 'Sleep performance and duration' },
                  { id: 'strain', icon: Zap, label: 'Strain', tooltip: 'Daily strain and activity' },
                  { id: 'compare', icon: TrendingUp, label: 'Compare', tooltip: 'Compare metrics over time' },
                  { id: 'history', icon: Table, label: 'History', tooltip: 'View historical data' }
                ].map((tab) => (
                  <Tooltip key={tab.id}>
                    <TooltipTrigger asChild>
                      <Button
                        variant={activeView === tab.id ? 'default' : 'outline'}
                        onClick={() => setActiveView(tab.id as ViewType)}
                        className="h-14 sm:h-16 flex flex-col items-center justify-center gap-0.5 sm:gap-1 hover:scale-[1.02] transition-all"
                      >
                        <tab.icon className="h-4 w-4 sm:h-5 sm:w-5" />
                        <span className="text-[10px] sm:text-xs font-light">{tab.label}</span>
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>{tab.tooltip}</TooltipContent>
                  </Tooltip>
                ))}
              </div>

              {/* Sync Buttons */}
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2">
                <p className="text-sm text-muted-foreground">
                  {lastSync && `Last synced: ${format(new Date(lastSync), 'MMM d, h:mm a')} • `}
                  {allWhoopData.length} days of data
                </p>
                <div className="flex gap-2">
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={handleFullHistorySync}
                        disabled={isFullSyncing || isSyncing}
                      >
                        <Download className={`h-4 w-4 mr-2 ${isFullSyncing ? 'animate-pulse' : ''}`} />
                        {isFullSyncing ? 'Importing...' : 'Full History'}
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>Import all your WHOOP history since you started using it</TooltipContent>
                  </Tooltip>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={handleSync}
                    disabled={isSyncing || isFullSyncing}
                  >
                    <RefreshCw className={`h-4 w-4 mr-2 ${isSyncing ? 'animate-spin' : ''}`} />
                    {isSyncing ? 'Syncing...' : 'Sync Now'}
                  </Button>
                </div>
              </div>

              {/* Overview Tab */}
              {activeView === 'overview' && (displayData || whoopDataForRange.length > 0) && (
                <div className="space-y-6 animate-fade-in">
                  {/* Main Metrics - Show averages for week/month, single day for day view */}
                  {(() => {
                    // Calculate averages for the time range
                    const dataToUse = whoopDataForRange.length > 0 ? whoopDataForRange : (displayData ? [displayData] : []);
                    
                    const validRecovery = dataToUse.filter(d => d.recovery_score != null);
                    const validHrv = dataToUse.filter(d => d.hrv_score != null);
                    const validSleep = dataToUse.filter(d => d.sleep_performance != null);
                    const validStrain = dataToUse.filter(d => d.strain_score != null);
                    const validRhr = dataToUse.filter(d => d.resting_heart_rate != null);
                    const validSleepDuration = dataToUse.filter(d => d.sleep_duration_minutes != null);
                    const validCalories = dataToUse.filter(d => d.calories_burned != null);

                    const avgRecovery = validRecovery.length > 0 
                      ? validRecovery.reduce((sum, d) => sum + (d.recovery_score || 0), 0) / validRecovery.length 
                      : null;
                    const avgHrv = validHrv.length > 0 
                      ? validHrv.reduce((sum, d) => sum + (d.hrv_score || 0), 0) / validHrv.length 
                      : null;
                    const avgSleep = validSleep.length > 0 
                      ? validSleep.reduce((sum, d) => sum + (d.sleep_performance || 0), 0) / validSleep.length 
                      : null;
                    const avgStrain = validStrain.length > 0 
                      ? validStrain.reduce((sum, d) => sum + (d.strain_score || 0), 0) / validStrain.length 
                      : null;
                    const avgRhr = validRhr.length > 0 
                      ? validRhr.reduce((sum, d) => sum + (d.resting_heart_rate || 0), 0) / validRhr.length 
                      : null;
                    const avgSleepDuration = validSleepDuration.length > 0 
                      ? validSleepDuration.reduce((sum, d) => sum + (d.sleep_duration_minutes || 0), 0) / validSleepDuration.length 
                      : null;
                    const avgCalories = validCalories.length > 0 
                      ? validCalories.reduce((sum, d) => sum + (d.calories_burned || 0), 0) / validCalories.length 
                      : null;

                    const periodLabel = timeRange === 'day' 
                      ? (isSameDay(selectedDate, new Date()) ? "Today's Metrics" : format(selectedDate, 'EEEE, MMMM d'))
                      : timeRange === 'week' 
                        ? '7-Day Averages' 
                        : '30-Day Averages';

                    return (
                      <>
                        <Card className="border-2 border-primary/30">
                          <CardHeader className="pb-2">
                            <CardTitle className="text-lg flex items-center gap-2">
                              <Activity className="h-5 w-5 text-primary" />
                              {periodLabel}
                            </CardTitle>
                            {timeRange !== 'day' && (
                              <CardDescription>
                                Based on {dataToUse.length} days of data
                              </CardDescription>
                            )}
                          </CardHeader>
                          <CardContent>
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                              <WhoopMetricCard
                                icon={Battery}
                                label={timeRange === 'day' ? 'Recovery' : 'Avg Recovery'}
                                value={avgRecovery ? Math.round(avgRecovery) : '--'}
                                unit="%"
                                colorClass={getRecoveryColor(avgRecovery)}
                                bgClass={avgRecovery && avgRecovery >= 67 ? 'bg-accent/10' : avgRecovery && avgRecovery >= 34 ? 'bg-primary/10' : 'bg-secondary/10'}
                                onClick={() => setActiveView('recovery')}
                              />
                              <WhoopMetricCard
                                icon={Heart}
                                label={timeRange === 'day' ? 'HRV' : 'Avg HRV'}
                                value={avgHrv ? Math.round(avgHrv) : '--'}
                                unit="ms"
                                colorClass="text-accent"
                                bgClass="bg-accent/5"
                                onClick={() => setActiveView('recovery')}
                              />
                              <WhoopMetricCard
                                icon={Moon}
                                label={timeRange === 'day' ? 'Sleep' : 'Avg Sleep'}
                                value={avgSleep ? Math.round(avgSleep) : '--'}
                                unit="%"
                                colorClass={getSleepColor(avgSleep)}
                                bgClass={avgSleep && avgSleep >= 85 ? 'bg-accent/10' : avgSleep && avgSleep >= 70 ? 'bg-primary/10' : 'bg-secondary/10'}
                                onClick={() => setActiveView('sleep')}
                              />
                              <WhoopMetricCard
                                icon={Zap}
                                label={timeRange === 'day' ? 'Strain' : 'Avg Strain'}
                                value={avgStrain ? avgStrain.toFixed(1) : '--'}
                                colorClass={getStrainColor(avgStrain)}
                                bgClass={avgStrain && avgStrain >= 14 ? 'bg-primary/10' : 'bg-secondary/10'}
                                onClick={() => setActiveView('strain')}
                              />
                            </div>
                          </CardContent>
                        </Card>

                        {/* Additional Metrics */}
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                          <WhoopMetricCard
                            icon={Heart}
                            label={timeRange === 'day' ? 'Resting HR' : 'Avg Resting HR'}
                            value={avgRhr ? Math.round(avgRhr) : '--'}
                            unit="bpm"
                            colorClass="text-secondary"
                            bgClass="bg-secondary/5"
                          />
                          <WhoopMetricCard
                            icon={Moon}
                            label={timeRange === 'day' ? 'Sleep Duration' : 'Avg Sleep'}
                            value={formatDuration(avgSleepDuration)}
                            colorClass="text-primary"
                            bgClass="bg-primary/5"
                          />
                          <WhoopMetricCard
                            icon={Activity}
                            label={timeRange === 'day' ? 'Calories' : 'Avg Calories'}
                            value={avgCalories ? Math.round(avgCalories).toLocaleString() : '--'}
                            colorClass="text-primary"
                            bgClass="bg-primary/5"
                          />
                          <WhoopMetricCard
                            icon={TrendingUp}
                            label="Days Tracked"
                            value={allWhoopData.length}
                            colorClass="text-accent"
                            bgClass="bg-accent/5"
                          />
                        </div>
                      </>
                    );
                  })()}

                  {/* Quick Stats - Based on selected time range */}
                  {whoopDataForRange.length > 0 && (() => {
                    // Calculate stats for the selected time range
                    const validRecovery = whoopDataForRange.filter(d => d.recovery_score != null);
                    const validHrv = whoopDataForRange.filter(d => d.hrv_score != null);
                    const validSleep = whoopDataForRange.filter(d => d.sleep_performance != null);
                    const validStrain = whoopDataForRange.filter(d => d.strain_score != null);

                    const avgRecovery = validRecovery.length > 0 
                      ? validRecovery.reduce((sum, d) => sum + (d.recovery_score || 0), 0) / validRecovery.length 
                      : 0;
                    const avgHrv = validHrv.length > 0 
                      ? validHrv.reduce((sum, d) => sum + (d.hrv_score || 0), 0) / validHrv.length 
                      : 0;
                    const avgSleep = validSleep.length > 0 
                      ? validSleep.reduce((sum, d) => sum + (d.sleep_performance || 0), 0) / validSleep.length 
                      : 0;
                    const avgStrain = validStrain.length > 0 
                      ? validStrain.reduce((sum, d) => sum + (d.strain_score || 0), 0) / validStrain.length 
                      : 0;

                    // Calculate trend
                    const midPoint = Math.floor(validRecovery.length / 2);
                    const recentAvg = midPoint > 0 
                      ? validRecovery.slice(0, midPoint).reduce((sum, d) => sum + (d.recovery_score || 0), 0) / midPoint 
                      : avgRecovery;
                    const olderAvg = midPoint > 0 
                      ? validRecovery.slice(midPoint).reduce((sum, d) => sum + (d.recovery_score || 0), 0) / (validRecovery.length - midPoint) 
                      : avgRecovery;
                    const diff = recentAvg - olderAvg;
                    const trend = diff > 5 ? 'improving' : diff < -5 ? 'declining' : 'stable';
                    
                    const periodLabel = timeRange === 'day' ? 'Today' : timeRange === 'week' ? '7-Day' : '30-Day';

                    return (
                      <Card>
                        <CardHeader className="pb-2">
                          <CardTitle className="text-lg flex items-center gap-2">
                            <TrendingUp className="h-5 w-5 text-primary" />
                            {periodLabel} Overview
                          </CardTitle>
                        </CardHeader>
                        <CardContent>
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                            <div className="text-center p-3 bg-accent/5 rounded-lg">
                              <p className={`text-2xl font-bold ${getRecoveryColor(avgRecovery)}`}>
                                {avgRecovery.toFixed(0)}%
                              </p>
                              <p className="text-xs text-muted-foreground">Avg Recovery</p>
                            </div>
                            <div className="text-center p-3 bg-accent/5 rounded-lg">
                              <p className="text-2xl font-bold text-accent">{avgHrv.toFixed(0)}</p>
                              <p className="text-xs text-muted-foreground">Avg HRV</p>
                            </div>
                            <div className="text-center p-3 bg-primary/5 rounded-lg">
                              <p className={`text-2xl font-bold ${getSleepColor(avgSleep)}`}>
                                {avgSleep.toFixed(0)}%
                              </p>
                              <p className="text-xs text-muted-foreground">Avg Sleep</p>
                            </div>
                            <div className="text-center p-3 bg-secondary/5 rounded-lg">
                              <p className={`text-2xl font-bold ${getStrainColor(avgStrain)}`}>
                                {avgStrain.toFixed(1)}
                              </p>
                              <p className="text-xs text-muted-foreground">Avg Strain</p>
                            </div>
                          </div>
                          
                          {/* Trend indicator - only show for week/month */}
                          {timeRange !== 'day' && (
                            <div className="mt-4 p-3 bg-muted/20 rounded-lg text-center">
                              <p className="text-sm">
                                <span className="font-medium">{periodLabel} Trend: </span>
                                <span className={trend === 'improving' ? 'text-accent' : trend === 'declining' ? 'text-secondary' : 'text-muted-foreground'}>
                                  {trend === 'improving' && '↑ Improving'}
                                  {trend === 'declining' && '↓ Declining'}
                                  {trend === 'stable' && '→ Stable'}
                                </span>
                              </p>
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    );
                  })()}
                </div>
              )}

              {/* Recovery Detail Tab */}
              {activeView === 'recovery' && (
                <WhoopRecoveryDetail 
                  data={whoopDataForRange}
                  selectedData={selectedDayData || latestCompleteData}
                  timeRange={timeRange}
                />
              )}

              {/* Sleep Detail Tab */}
              {activeView === 'sleep' && (
                <WhoopSleepDetail 
                  data={whoopDataForRange}
                  selectedData={selectedDayData || latestCompleteData}
                  timeRange={timeRange}
                />
              )}

              {/* Strain Detail Tab */}
              {activeView === 'strain' && (
                <WhoopStrainDetail 
                  data={whoopDataForRange}
                  selectedData={selectedDayData || latestCompleteData}
                  timeRange={timeRange}
                />
              )}

              {/* Compare View Tab - Full feature set for ALL users */}
              {activeView === 'compare' && (
                <div className="space-y-6 animate-fade-in">
                  <WhoopCompareView />
                </div>
              )}

              {/* History Table Tab */}
              {activeView === 'history' && (
                <div className="space-y-6 animate-fade-in">
                  <WhoopHistoryTable 
                    data={allWhoopData}
                    onRowClick={handleRowClick}
                    onCompareClick={(data) => {
                      setSelectedDate(parseISO(data.date));
                      setActiveView('compare');
                      toast.success(`Viewing comparison for ${format(parseISO(data.date), 'MMM d')}`);
                    }}
                  />
                </div>
              )}
            </>
          )}

          {/* No data state */}
          {isConnected && allWhoopData.length === 0 && (
            <Card className="border-primary/30">
              <CardContent className="py-12 text-center space-y-4">
                <Activity className="h-12 w-12 text-primary mx-auto" />
                <h3 className="text-lg font-semibold">No WHOOP Data Yet</h3>
                <p className="text-sm text-muted-foreground max-w-md mx-auto">
                  Click "Sync Now" to pull your WHOOP data. Make sure you have data recorded in your WHOOP app.
                </p>
                <Button onClick={handleSync} disabled={isSyncing}>
                  <RefreshCw className={`h-4 w-4 mr-2 ${isSyncing ? 'animate-spin' : ''}`} />
                  {isSyncing ? 'Syncing...' : 'Sync WHOOP Data'}
                </Button>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
